/**
 * <Icon> — wrapper around lucide-react that locks icon sizing to the
 * approved scale (16 / 20 / 24 / 32) and enforces accessibility props.
 *
 * Resolves Phase-1 finding M4: 70 of 106 sampled icon-size declarations
 * are non-standard (64px, 80px, w-7, w-10, w-12, etc.). The codemod that
 * migrates existing usages runs in Phase 13.
 *
 * Use:
 *
 *   import { ChevronDown } from 'lucide-react';
 *   import { Icon } from '@/design-system';
 *
 *   <Icon as={ChevronDown} size="sm" />                  // decorative, hidden from a11y
 *   <Icon as={ChevronDown} size="md" label="Sort by date" /> // informative, labeled
 *
 * Sizes:
 *   - `xs`  — 16px — inline with body text, dense table cells
 *   - `sm`  — 20px — buttons, action chips (default)
 *   - `md`  — 24px — toolbar, navigation
 *   - `lg`  — 32px — empty-state hero icons
 *
 * Larger sizes (48 / 64 / 80) are intentionally NOT supported; if you
 * need a "feature graphic" use an SVG illustration, not a Lucide glyph.
 *
 * Spec: docs/design-system/04-color-and-iconography.md.
 */

import type { ComponentType } from 'react';
import { cn } from '../utils/cn';

export type IconSize = 'xs' | 'sm' | 'md' | 'lg';

/**
 * Minimal subset of LucideProps. Avoids tight coupling to lucide-react's
 * internal type without giving up type safety on the few props we actually pass.
 */
interface BaseIconComponentProps {
  size?: number | string;
  width?: number | string;
  height?: number | string;
  strokeWidth?: number | string;
  className?: string;
  role?: string;
  'aria-label'?: string;
  'aria-hidden'?: boolean | 'true' | 'false';
  color?: string;
  fill?: string;
  stroke?: string;
}

export interface IconProps {
  /** Lucide icon component (e.g. `ChevronDown` from `lucide-react`). */
  as: ComponentType<BaseIconComponentProps>;
  /** Size token. Default `sm` (20px). */
  size?: IconSize;
  /**
   * Accessible label. When supplied, renders `role="img"` and `aria-label`.
   * When omitted, the icon is treated as decorative — `aria-hidden="true"`.
   */
  label?: string;
  className?: string;
  /** Inline color override (rare — prefer Tailwind text-* classes via className). */
  color?: string;
  /** Stroke width — defaults to 1.75 per design-system spec. */
  strokeWidth?: number;
}

const SIZE_CLASSES: Record<IconSize, string> = {
  xs: 'w-4 h-4',  // 16px
  sm: 'w-5 h-5',  // 20px — default
  md: 'w-6 h-6',  // 24px
  lg: 'w-8 h-8',  // 32px
};

const SIZE_PIXELS: Record<IconSize, number> = {
  xs: 16,
  sm: 20,
  md: 24,
  lg: 32,
};

export function Icon({
  as: Component,
  size = 'sm',
  label,
  className,
  color,
  strokeWidth = 1.75,
}: IconProps) {
  const a11y = label
    ? { role: 'img', 'aria-label': label }
    : ({ 'aria-hidden': 'true' } as const);

  return (
    <Component
      // Pass numeric `size` so SVG path geometry stays sharp at any DPR;
      // the className locks the rendered box for layout.
      width={SIZE_PIXELS[size]}
      height={SIZE_PIXELS[size]}
      className={cn(SIZE_CLASSES[size], 'shrink-0', className)}
      strokeWidth={strokeWidth}
      color={color}
      {...a11y}
    />
  );
}

Icon.displayName = 'Icon';

export default Icon;
